import { useState } from "react";
import type { Conversation } from "@swift/shared";
import { api } from "../lib/api";
import { IconPoll, IconClose } from "./icons";

/**
 * Modale poll-bouwer die de Composer triggert. Vraag + minimaal twee
 * opties, met "meerdere antwoorden toegestaan"-checkbox. Bij verzending
 * komt er een nieuw bericht in het gesprek met een poll-payload; de
 * server doet de DB-koppeling. Sluiten via ✕ of buiten de modal klikken
 * (Esc nemen we mee gratis door de focus-handler).
 */
export default function PollComposer({
  conversation,
  onClose,
}: {
  conversation: Conversation;
  onClose(): void;
}) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setOption(idx: number, value: string) {
    setOptions((arr) => arr.map((o, i) => (i === idx ? value : o)));
  }
  function addOption() {
    if (options.length >= 10) return;
    setOptions((arr) => [...arr, ""]);
  }
  function removeOption(idx: number) {
    if (options.length <= 2) return;
    setOptions((arr) => arr.filter((_, i) => i !== idx));
  }

  async function submit() {
    setError(null);
    const q = question.trim();
    if (q.length === 0) {
      setError("Geef een vraag op");
      return;
    }
    const opts = options.map((o) => o.trim()).filter((o) => o.length > 0);
    const uniq = [...new Set(opts)];
    if (uniq.length < 2) {
      setError("Geef minimaal twee verschillende opties");
      return;
    }
    setBusy(true);
    try {
      await api.createPoll(conversation.id, {
        question: q,
        options: uniq,
        allow_multiple: allowMultiple,
      });
      onClose();
    } catch {
      setError("Poll maken mislukt");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 pt-12"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-md flex-col rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-slate-700 bg-slate-800 px-4 py-2">
          <IconPoll size={16} className="shrink-0 text-slate-400" />
          <h2 className="flex-1 text-sm font-medium text-slate-200">Nieuwe poll</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
            aria-label="Sluiten"
            disabled={busy}
          >
            <IconClose size={16} />
          </button>
        </div>
        <div className="space-y-3 p-4 text-sm">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-400">
              Vraag
            </label>
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={280}
              placeholder="Wanneer plannen we de meeting?"
              className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none focus:border-accent"
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-400">
              Opties
            </label>
            <div className="space-y-1.5">
              {options.map((o, i) => (
                <div key={i} className="flex items-center gap-1">
                  <input
                    type="text"
                    value={o}
                    onChange={(e) => setOption(i, e.target.value)}
                    maxLength={140}
                    placeholder={`Optie ${i + 1}`}
                    className="flex-1 rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-slate-100 outline-none focus:border-accent"
                  />
                  {options.length > 2 && (
                    <button
                      onClick={() => removeOption(i)}
                      className="rounded p-1 text-slate-500 hover:bg-slate-700 hover:text-red-400"
                      aria-label="Verwijderen"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 10 && (
              <button
                onClick={addOption}
                className="mt-1 text-xs text-accent hover:underline"
              >
                + optie toevoegen
              </button>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={allowMultiple}
              onChange={(e) => setAllowMultiple(e.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            Meerdere antwoorden toegestaan
          </label>
          {error && <div className="text-xs text-red-400">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-700 bg-slate-800 px-4 py-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded bg-slate-700 px-3 py-1 text-sm text-slate-200 hover:bg-slate-600 disabled:opacity-50"
          >
            Annuleer
          </button>
          <button
            onClick={() => void submit()}
            disabled={busy}
            className="rounded bg-accent px-3 py-1 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? "Bezig…" : "Posten"}
          </button>
        </div>
      </div>
    </div>
  );
}
